import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { MACHINES } from '../engineeringRules';

export default function Machines() {
  const [selected, setSelected] = useState<string | null>(null);
  const all = Object.values(MACHINES) as any[];
  const bolt = all.filter(m => m.type === 'BOLT');
  const nut = all.filter(m => m.type === 'NUT');
  const sel: any = selected ? (MACHINES as any)[selected] : null;

  return (
    <div className="fade-in">
      <div className="section-header">
        <h1>Machine Fleet</h1>
        <span className="badge badge-blue">{all.length} machines · {all.reduce((a, m) => a + m.qty, 0)} units</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 360px' : '1fr', gap: 20 }}>
        <div>
          <h4 style={{ marginBottom: 12 }}>Bolt Formers</h4>
          <div className="grid-2" style={{ marginBottom: 24 }}>
            {bolt.map((m: any) => (
              <MachineCard key={m.id} machine={m} selected={selected === m.id}
                onClick={() => setSelected(selected === m.id ? null : m.id)} />
            ))}
          </div>
          <h4 style={{ marginBottom: 12 }}>Nut Formers</h4>
          <div className="grid-2">
            {nut.map((m: any) => (
              <MachineCard key={m.id} machine={m} selected={selected === m.id}
                onClick={() => setSelected(selected === m.id ? null : m.id)} />
            ))}
          </div>
        </div>

        {sel && (
          <div className="card fade-in" style={{ alignSelf: 'start', position: 'sticky', top: 24 }}>
            <div className="card-header">
              <h3>{sel.name}</h3>
              <span className={`badge ${sel.type === 'BOLT' ? 'badge-orange' : 'badge-blue'}`}>{sel.type}</span>
            </div>
            <ConstRow label="Force" value={`${(sel.force_kg / 1000).toFixed(0)} tonnes`} />
            <ConstRow label="Stations" value={`${sel.stations}`} />
            <ConstRow label="Quantity" value={`${sel.qty} unit${sel.qty > 1 ? 's' : ''}`} />
            <ConstRow label="Wire range" value={`Ø${sel.wire_min}–${sel.wire_max}mm`} />
            {sel.cbf && sel.cbf !== '—' && <ConstRow label="CBF" value={sel.cbf} />}

            {sel.type === 'BOLT' && sel.die_case_od && (
              <>
                <hr className="divider" />
                <h4 style={{ marginBottom: 8 }}>Confirmed Constants</h4>
                <ConstRow label="Die case OD" value={`Ø${sel.die_case_od}mm -0.02`} />
                <ConstRow label="Die length" value={`${sel.die_len}mm`} />
                <ConstRow label="KO pin length" value={`${sel.ko_pin_len}mm`} />
                <ConstRow label="Punch case OD" value={`Ø${sel.punch_case_od}mm`} />
                <ConstRow label="Punch case len" value={`${sel.punch_case_len}mm`} />
                <ConstRow label="Concentricity" value="TIR 0.020mm" />
                <ConstRow label="Perpendicularity" value="TIR 0.020mm" />
                <ConstRow label="KO head flange" value="Ø25.0 ±0.10mm EN-24" />
              </>
            )}

            {sel.type === 'NUT' && sel.ko_pin_len && (
              <>
                <hr className="divider" />
                <h4 style={{ marginBottom: 8 }}>Confirmed Constants</h4>
                <ConstRow label="Nut size range" value={sel.nut_size} />
                <ConstRow label="A/F range" value={`${sel.af_min}–${sel.af_max}mm`} />
                <ConstRow label="KO pin length" value={`${sel.ko_pin_len}mm`} />
                <ConstRow label="Punch total len" value={`${sel.punch_len}mm`} />
                <ConstRow label="Punch working" value={`${sel.punch_working_len}mm`} />
                <ConstRow label="Die height" value={`${sel.die_height_mm}mm`} />
                <ConstRow label="Die OD taper" value={`${sel.die_taper_deg}°`} />
                <ConstRow label="Finger length" value={`${sel.finger_len}mm`} />
                <ConstRow label="Finger body OD" value={`Ø${sel.finger_body_od}mm`} />
                <ConstRow label="Tail taper" value={`${sel.finger_tail_taper}°`} />
                <ConstRow label="Finger width" value={`${sel.finger_width}mm`} />
                {sel.finger_thickness && <ConstRow label="Finger thickness" value={`${sel.finger_thickness}mm`} />}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MachineCard({ machine, selected, onClick }: { machine: any; selected: boolean; onClick: () => void }) {
  const hasConstants = machine.die_case_od || machine.ko_pin_len;
  return (
    <div className="card card-hover" onClick={onClick} style={{
      border: selected ? '1px solid var(--forge-accent)' : '1px solid var(--forge-border)',
      background: selected ? 'var(--forge-accent-dim2)' : 'var(--forge-surface)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{machine.name}</div>
          <div style={{ fontSize: 10, color: 'var(--forge-text3)', marginTop: 2 }}>
            {machine.type === 'NUT' ? machine.nut_size : `Ø${machine.wire_min}–${machine.wire_max}mm`}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span className={`badge ${machine.type === 'BOLT' ? 'badge-orange' : 'badge-blue'}`}>{machine.type}</span>
          {hasConstants && (
            <span className="badge badge-green" style={{ fontSize: 9 }}>
              <CheckCircle size={8} /> Confirmed
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--forge-text2)' }}>
        <span>{(machine.force_kg / 1000).toFixed(0)}t · {machine.stations}stn</span>
        <span style={{ color: 'var(--forge-text3)' }}>×{machine.qty}</span>
      </div>
    </div>
  );
}

function ConstRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--forge-border)', fontSize: 12 }}>
      <span style={{ color: 'var(--forge-text2)' }}>{label}</span>
      <span className="mono-val">{value}</span>
    </div>
  );
}
